  // src/hooks/useQuickServiceQueries.js
  // React Query hooks for crane & recovery quick services.
  // Advocate hooks already exist in this file — add them below as needed.

  import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
  import api from "../axiosInstance";

  // ─── Query keys ───────────────────────────────────────────────────────────────

  export const QS_KEYS = {
    list:    (filters) => ["quick-services", filters],
    detail:  (id)      => ["quick-service", id],
    stats:   (serviceType = "all") => ["quick-services-stats", serviceType],
    tracking:(id)      => ["qs-tracking", id],
  };

  // ─── Normalizers ──────────────────────────────────────────────────────────────

  /**
   * Normalize a raw quick_service_requests row from the backend JOIN response
   * into a stable shape the UI can rely on.
   */
  function normalizeRequest(r) {
    return {
      // ── Identity ──────────────────────────────────────────────────────────────
      id:           r.id,
      requestCode:  r.request_code,
      serviceType:  r.service_type,                 // 'crane' | 'recovery'
      source:       r.source,                       // 'whatsapp_bot' | 'admin'
      status:       r.status,
      urgent:       !!r.urgent,
      createdAt:    r.created_at,
      updatedAt:    r.updated_at,
      createdBy:    r.created_by || null,
      accidentCaseNo: r.accident_case_no || null,

      // ── Incident ──────────────────────────────────────────────────────────────
      vehicleNumber:      r.vehicle_number      || null,
      pickupLocation:     r.pickup_location     || null,
      dropLocation:       r.drop_location       || null,
      pickupLat:          r.pickup_lat          || null,
      pickupLng:          r.pickup_lng          || null,
      dropLat:            r.drop_lat            || null,
      dropLng:            r.drop_lng            || null,
      issueDescription:   r.issue_description   || null,

      // ── Owner / driver details (step 1) ───────────────────────────────────────
      customerName:      r.owner_name     || null,
      customerContact:   r.owner_contact  || null,
      driverName:     r.driver_name    || null,
      driverContact:  r.driver_contact || null,

      // ── Vehicle selection (step 2) ────────────────────────────────────────────
      supportVehicleType: r.support_vehicle_type || null,
      // 'hydra_crane' | 'recovery_non_flatbed' | 'recovery_flatbed'
      supportVehicleSize: r.support_vehicle_size || null,
      // 'mini' | 'medium' | 'large'

      // ── Vendor assignment (step 3) ────────────────────────────────────────────
      vendorId:                 r.vendor_id                  || null,
      vendorName:               r.vendorName                 || null,
      vendorPhone:              r.vendorPhone                || null,
      vendorContactPerson:      r.vendorContactPerson        || null,
      vendorAddress:            r.vendorAddress              || null,
      vendorDistrict:           r.vendorDistrict             || null,
      vendorLat:                r.vendorLat                  || null,
      vendorLng:                r.vendorLng                  || null,

      //pending request fields (for step 4 and OTP verification)
      approvedReaching:        r.approvedReaching          || null,
      approvedReachingTime:    r.approvedReachingTime      || null,
      customerAcceptedVendor:  r.customerAcceptedVendor    || null,
      approvedRightlyMeasured: r.approvedRightlyMeasured   || null,
      otpByCustomer:           r.otpByCustomer             || null,
      otp:                     r.otp                       || null,
      confirmDoneWorking:      r.confirmDoneWorking         || null,
      confirmDoneWorkingTime:  r.confirmDoneWorkingTime     || null,
      vendorMoved:          r.vendorMoved                || null,
      vendorReached:        r.vendorReached              || null,
      timeDiffRToC:          r.timeDiffRToC              || null,
      amountOfRtoC:        r.amountOfRtoC              || null,

      // ── Vehicle registration (step 3) ────────────────────────────────────────
      qsVehicleId:              r.qs_vehicle_id              || null,
      vrRegistrationNumber:     r.vr_registration_number     || null,
      vrOwnerName:              r.vr_owner_name              || null,
      vrChassisNumber:          r.vr_chassis_number          || null,
      vrEngineNumber:           r.vr_engine_number           || null,
      vrMaker:                  r.vr_maker                   || null,
      vrModel:                  r.vr_model                   || null,
      vrInsuranceCompany:       r.vr_insurance_company       || null,
      vrInsuranceValidUpto:     r.vr_insurance_valid_upto    || null,
      vrFitnessValidUpto:       r.vr_fitness_valid_upto      || null,
      vrColor:                  r.vr_color                   || null,
      vrFuelType:               r.vr_fuel_type               || null,

      // ── Dispatched driver (overridable in step 3) ─────────────────────────────
      dispatchedDriverName:     r.dispatched_driver_name     || null,
      dispatchedDriverContact:  r.dispatched_driver_contact  || null,
      driverLat:                r.driver_lat                  || null,
      driverLng:                r.driver_lng                  || null,
      driverlastPing:           r.driver_last_ping            || null,

      //-----Payment data ----
      charges: r.charges || null,
      rate_per_km : r.rate_per_km ||null,
      km_variation_pct: r.km_variation_pct || null,

      // ── Photos ────────────────────────────────────────────────────────────────
      // accident_photos is a JSON column: { front_left, front_right, rear_left,
      // rear_right, additional: [] }. Backend already parses it; guard here too.
      accidentPhotos: r.accident_photos
        ? (typeof r.accident_photos === "string"
            ? JSON.parse(r.accident_photos)
            : r.accident_photos)
        : { front_left: null, front_right: null, rear_left: null, rear_right: null, additional: [] },

      // ── WhatsApp linkback ─────────────────────────────────────────────────────
      whatsappSessionId: r.whatsapp_session_id || null,
    };
  }

  // ─── Queries ──────────────────────────────────────────────────────────────────

  /**
   * List quick service requests with optional filters.
   * Filters: service_type, status, search, urgent, cursor, limit
   */
 export function useQuickServiceRequests(filters = {}) {
  return useInfiniteQuery({
    queryKey: QS_KEYS.list(filters),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") params.append(k, v);
      });
      if (pageParam) params.append("cursor", pageParam);

      const { data } = await api.get(`/api/admin/quick-services?${params}`);
      const raw = data?.data || [];

      return {
        data:       raw.map(normalizeRequest),
        nextCursor: data?.nextCursor ?? null,   // ★ must come straight from backend
        total:      data?.total ?? raw.length,
      };
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined, // ★ critical
    placeholderData: (prev) => prev,
  });
}

  /**
   * Stat cards: awaitingAssignment, inProgress, completedToday
   */
  export function useQuickServiceStats(serviceType = "all", options = {}) {
  return useQuery({
    queryKey: QS_KEYS.stats(serviceType),
    queryFn: async () => {
      const params = serviceType === "crane" || serviceType === "recovery"
        ? `?service_type=${serviceType}`
        : "";
      const { data } = await api.get(`/api/admin/quick-services/stats${params}`);
      const d = data?.data || {};
      return {
        allCases:           d.allCases           ?? 0,
        awaitingAssignment: d.awaitingAssignment  ?? 0,
        inProgress:         d.inProgress          ?? 0,
        completedToday:     d.completedToday      ?? 0,
        craneCount:         d.craneCount          ?? 0,
        recoveryCount:      d.recoveryCount       ?? 0,
      };
    },
    enabled: options.enabled ?? true,
    refetchInterval: 60000,
  });
}

  /**
   * Single request — full detail with vendor + fleet JOINs
   */
  export function useQuickServiceDetail(id) {
    return useQuery({
      queryKey: QS_KEYS.detail(id),
      queryFn: async () => {
        const { data } = await api.get(`/api/admin/quick-services/${id}`);
        return normalizeRequest(data?.data || data);
      },
      enabled: !!id,
    });
  }

  export function useQuickServiceTimeline(requestCode) {
    return useQuery({
      queryKey: ["qs-timeline", requestCode],
      queryFn: async () => {
        const { data } = await api.get(`/api/admin/quick-services/${requestCode}/timeline`);
        return data?.data || [];
      },
      enabled: !!requestCode,
    });
  }

  export function useUpdateQuickServiceStatusDirect(requestCode) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ status, reason }) => {
      const { data } = await api.patch(
        `/api/admin/quick-services/${requestCode}/status`,
        { status, reason, force: true }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) }); // ← fixed key
      queryClient.invalidateQueries({ queryKey: ["qs-timeline", requestCode] });
      queryClient.invalidateQueries({ queryKey: ["quick-services"] }); // list view too
    },
  });
}

  /**
   * Fleet vehicle lookup by plate number — used in the assign step.
   * Returns { found, data: { id, vehicleNo, ownerName, ownerContact, ... } }
   */
  export function useVehicleRegistrationLookup(plate) {
    return useQuery({
      queryKey: ["vr-lookup", plate],
      queryFn: async () => {
        const { data } = await api.get(
          `/api/admin/vehicle-registrations/lookup?plate=${encodeURIComponent(plate)}`
        );
        return {
          found: data?.found ?? false,
          data:  data?.data  || null,
        };
      },
      // Only fire when admin has typed at least a partial plate
      enabled: !!plate && plate.trim().length >= 4,
      // Lookup results are stable — cache for 10 min
      staleTime: 1000 * 60 * 10,
      // Don't retry on 404 (not found is a valid result, not an error)
      retry: (count, error) => error?.response?.status !== 404 && count < 2,
    });
  }

  /**
   * Latest live tracking ping — polled every 10 s when the live modal is open.
   * Only call this hook when the modal is visible (pass enabled = true).
   */
  export function useQSTracking(requestId, { enabled = false } = {}) {
    return useQuery({
      queryKey: QS_KEYS.tracking(requestId),
      queryFn: async () => {
        const { data } = await api.get(
          `/api/admin/quick-services/${requestId}/tracking`
        );
        return data?.data || null;
      },
      enabled: !!requestId && enabled,
      refetchInterval: 10000,        // poll every 10 s
      refetchIntervalInBackground: false,
    });
  }

  // ─── Mutations ────────────────────────────────────────────────────────────────

  /**
   * Admin creates a crane / recovery request manually.
   */
  export function useCreateQuickServiceRequest() {
    const qc = useQueryClient();
    
    return useMutation({
      mutationFn: async (payload) => {
        console.log(payload);
        const { data } = await api.post("/api/admin/quick-services", payload);
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["quick-services"] });
        qc.invalidateQueries({ queryKey: QS_KEYS.stats() });
        qc.invalidateQueries({ queryKey: ["case"] });
      },
    });
  }

  /**
   * Step 1 — save owner / driver details.
   * Status: NEW → DETAILS_PENDING
   */
  export function useUpdateQSDetails(requestCode) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async ({ customerName, driverName, driverContact }) => {
        const { data } = await api.patch(
          `/api/admin/quick-services/${requestCode}/details`,
          {
            owner_name:     customerName || null,
            driver_name:    driverName   || null,
            driver_contact: driverContact || null,
          }
        );
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) });
        qc.invalidateQueries({ queryKey: ["quick-services"] });
        qc.invalidateQueries({ queryKey: QS_KEYS.stats() });
      },
    });
  }

  /**
   * Step 2 — pick support vehicle type + size.
   * Status: DETAILS_PENDING → VEHICLE_SELECTED
   */
  export function useUpdateQSVehicleSelection(requestCode) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async ({ supportVehicleType, supportVehicleSize }) => {
        const { data } = await api.patch(
          `/api/admin/quick-services/${requestCode}/vehicle-selection`,
          {
            support_vehicle_type: supportVehicleType,
            support_vehicle_size: supportVehicleSize,
          }
        );
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) });
        qc.invalidateQueries({ queryKey: ["quick-services"] });
        qc.invalidateQueries({ queryKey: QS_KEYS.stats() });
      },
    });
  }

  /**
   * Step 3 — assign vendor + vehicle + dispatched driver.
   * Status: VEHICLE_SELECTED → ASSIGNED
   *
   * If vehicleNumber is sent without qsVehicleId, the backend looks it up in
   * fleet_list and creates a new row if not found.
   */
  export function useAssignQSVendor() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async ({ requestCode, vendorId, vendorName, vendorPhone }) => {
        console.log("dataaaaaaaaaaaa------------",requestCode, vendorId, vendorName, vendorPhone);
        const { data } = await api.patch(
          `/api/admin/quick-services/${requestCode}/assign-vendor`,
          { vendor_id: vendorId, vendor_name: vendorName, vendor_phone: vendorPhone }
        );
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["quick-services"] });
        qc.invalidateQueries({ queryKey: QS_KEYS.stats() });
      },
    });
  }

export function useRouteDistance(requestCode, enabled = true) {
  return useQuery({
    queryKey: [...QS_KEYS.detail(requestCode), "route-distance"],
    queryFn:  () =>
      api.get(`/api/admin/quick-services/${requestCode}/route-distance`).then(r => r.data),
        enabled:  enabled && !!requestCode,
        staleTime: Infinity,   // road distance won't change — never refetch
        retry: false,          // don't hammer OLA on 422 (no coords)
  });
}

// in useCraneQueries.js
export function useUpdateQSCharges(requestCode) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ratePerKm, charges }) =>
      api.patch(`/api/admin/quick-services/${requestCode}/charges`, { ratePerKm, charges }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) });
      queryClient.invalidateQueries({ queryKey: ["quick-services"] });
    },
  });
}

  export function useAdminConfirmPayment(requestCode) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async ({ paymentMode, customerCharge, lowChargeReason }) => {
        // We need AccidentVehicleCode which is the requestCode for QS cases
        const { data } = await api.post('/api/admin/qs/confirm-payment', {
          AccidentVehicleCode: requestCode,
          paymentMode,
          customerCharge,
          lowChargeReason,
        });
        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) });
        queryClient.invalidateQueries({ queryKey: ["quick-services"] });
        queryClient.invalidateQueries({ queryKey: QS_KEYS.stats() });
      },
    });
  }

  export function useAdminConfirmReached(requestCode) {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: () =>
        api
          .post(`/api/admin/qs/${requestCode}/confirm-reached`)
          .then(r => r.data),

      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) });
        queryClient.invalidateQueries({ queryKey: ["quick-services"] });
        queryClient.invalidateQueries({ queryKey: QS_KEYS.stats() });
        queryClient.invalidateQueries({ queryKey: QS_KEYS.stats()});
      },

      onError: (err) => {
        console.error("[useAdminConfirmReached]", err);
      },
    });
  }

  export function useAdminConfirmInspection(requestCode) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: () =>
        api.post(`/api/admin/qs/${requestCode}/confirm-inspection`).then(r => r.data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["quick-services"] });
        queryClient.invalidateQueries({ queryKey: ["quick-service", requestCode] });
        queryClient.invalidateQueries({ queryKey: QS_KEYS.stats()
  });
      },
    });
  }

  export function useAdminConfirmOTP(requestCode) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (otp) =>
        api.post(`/api/admin/qs/${requestCode}/confirm-otp`, { otp }).then(r => r.data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["quick-services"] });
        queryClient.invalidateQueries({ queryKey: ["quick-service", requestCode] });
         queryClient.invalidateQueries({queryKey: QS_KEYS.stats()});
      },
    });
  }

  export function useAdminConfirmDone(requestCode) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: () =>
        api.post(`/api/admin/qs/${requestCode}/confirm-done`).then(r => r.data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["quick-services"] });
        queryClient.invalidateQueries({ queryKey: ["quick-service", requestCode] });
         queryClient.invalidateQueries({ queryKey: QS_KEYS.stats()
  });
      },
    });
  }

  export function useQSTrackingHistory(requestCode, enabled = false) {
    return useQuery({
      queryKey: ["qs-tracking", requestCode],
      queryFn: () =>
        api.get(`/admin/quick-services/${requestCode}/tracking`).then(r => r.data),
      enabled,
      refetchInterval: false,
    });
  }

  export function useVendorVehicles(vendorCode) {
    return useQuery({
      queryKey: ["vendor-vehicles", vendorCode],
      queryFn: async () => {
        const { data } = await api.get(
          `/api/admin/quick-services/vendor-vehicles/${vendorCode}`
        );
        return data;
      },
      enabled: !!vendorCode,   // only fetch when vendorCode is available
    });
  }

  export function useAssignQSVehicle(requestId) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async ({ qsVehicleId, registrationNumber, dispatchedDriverName, dispatchedDriverContact }) => {
        console.log("Assigning vehicle", { qsVehicleId, registrationNumber, dispatchedDriverName, dispatchedDriverContact });
        const { data } = await api.patch(
          `/api/admin/quick-services/${requestId}/assign-vehicle`,
          {
            qs_vehicle_id:              qsVehicleId             || null,
            registration_number:        registrationNumber           || null,
            dispatched_driver_name:     dispatchedDriverName    || null,
            dispatched_driver_contact:  dispatchedDriverContact || null,
          }
        );
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: QS_KEYS.detail(requestId) });
        qc.invalidateQueries({ queryKey: ["quick-services"] });
      },
    });
  }

  export function useFetchAndSaveVehicle() {
    return useMutation({
      mutationFn: async (vehicleNumber) => {
        const plate = vehicleNumber.trim().toUpperCase();

        // 1. Fetch from ULIP (always — to get latest data)
        const ulipRes = await api.post("/api/ulip/fetch-and-save", {
          vehicleNumber: plate,
        });

        return ulipRes.data;
      },
    });
  }

  /**
   * Generic status transition: EN_ROUTE / REACHED / DONE / CANCELLED.
   * (Steps 1–3 have their own mutations above that handle status internally.)
   */
  export function useUpdateQSStatus(requestCode) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (status) => {
        const { data } = await api.patch(
          `/api/admin/quick-services/${requestCode}/status`,
          { status }
        );
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) });
        qc.invalidateQueries({ queryKey: ["quick-services"] });
        qc.invalidateQueries({ queryKey: QS_KEYS.stats() });
      },
    });
  }

  /**
   * Upload accident photos.
   * Accepts a FormData object with any of:
   *   front_left, front_right, rear_left, rear_right (single File each)
   *   additional (multiple Files)
   */
  export function useUpdateQSPhotos(requestCode) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (formData) => {
        const { data } = await api.patch(
          `/api/admin/quick-services/${requestCode}/photos`,
          formData
          // axios detects FormData and sets multipart/form-data automatically
        );
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) });
      },
    });
  }

  export function useDeleteQSPhoto(requestCode) {
    console.log("requestCode", requestCode);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ slot, photoUrl }) => {
      const { data } = await api.delete(
        `/api/admin/quick-services/${requestCode}/photos`,
        { data: { slot, photoUrl } } // axios needs body wrapped in { data } for DELETE
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QS_KEYS.detail(requestCode) });
      qc.invalidateQueries({ queryKey: ["quick-services"] });
    },
  });
}

  /**
   * Driver app posts a live location ping.
   * Called by the vendor-facing side, not the admin dashboard.
   */
  export function usePostQSTracking(requestId) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async ({ lat, lng, speedKmh = null }) => {
        const { data } = await api.post(
          `/api/admin/quick-services/${requestId}/tracking`,
          { lat, lng, speed_kmh: speedKmh }
        );
        return data;
      },
      onSuccess: () => {
        // Immediately refresh the tracking query so the live modal updates
        qc.invalidateQueries({ queryKey: QS_KEYS.tracking(requestId) });
      },
    });
  }